// Docs: https://www.instantdb.com/docs/modeling-data

import { i } from "@instantdb/react";

const _schema = i.schema({
  entities: {
    $files: i.entity({
      path: i.string().unique().indexed(),
      url: i.string(),
    }),
    $streams: i.entity({
      abortReason: i.string().optional(),
      clientId: i.string().unique().indexed(),
      done: i.boolean().optional(),
      size: i.number().optional(),
    }),
    $users: i.entity({
      email: i.string().unique().indexed().optional(),
      fullName: i.string().optional(),
      imageURL: i.string().optional(),
      type: i.string().optional(),
    }),
    chatMessages: i.entity({
      content: i.string(),
      createdAt: i.date().indexed(),
      fileUrl: i.string().optional(),
      messageType: i.string(),
      replyTo: i.string().optional(),
      updatedAt: i.date().optional(),
      userId: i.string().indexed(),
      userName: i.string(),
    }),
    clientRecommendations: i.entity({
      createdAt: i.date().optional(),
      iconColors: i.any().optional(),
      nonTrainingDayPlan: i.any().optional(),
      personalNote: i.string().optional(),
      updatedAt: i.date().optional(),
      userId: i.string().unique().indexed(),
    }),

    readingRequests: i.entity({
      createdAt: i.date().indexed(),
      email: i.string().indexed(),
      firstName: i.string().optional(),
      unsubscribeToken: i.string().unique().indexed(),
      unsubscribedAt: i.date().optional(),
      day3SentAt: i.date().optional(),
      day6SentAt: i.date().optional(),
    }),
    guideRequests: i.entity({
      // Leads from the "Which Starting-Over Story Is Yours?" assessment's
      // free-guide email signup. Kept separate from readingRequests (the
      // First Step reading funnel) so a shared email address can't collide
      // across the two different lead magnets.
      createdAt: i.date().indexed(),
      email: i.string().indexed(),
      firstName: i.string().optional(),
      resultKey: i.string().optional(),
      unsubscribeToken: i.string().unique().indexed(),
      unsubscribedAt: i.date().optional(),
    }),
    consultationRequests: i.entity({
      ageRange: i.string().optional(),
      biggestChallenge: i.string().optional(),
      createdAt: i.date().indexed(),
      email: i.string().indexed(),
      fitnessLevel: i.string().optional(),
      format: i.string(),
      fullName: i.string(),
      goals: i.string(),
      injuries: i.string().optional(),
      interestedIn: i.any(),
      location: i.string().optional(),
      manageToken: i.string().optional(),
      manageTokenExpiresAt: i.date().optional(),
      notes: i.string().optional(),
      phone: i.string(),
      preferredDate: i.string().indexed(),
      preferredTime: i.string(),
      referralSource: i.string().optional(),
      status: i.string().indexed(),
    }),
    packages: i.entity({
      createdAt: i.date().indexed(),
      description: i.string().optional(),
      durationDays: i.number(),
      features: i.any().optional(),
      isActive: i.boolean().indexed(),
      name: i.string(),
      packageType: i.string().indexed(),
      price: i.number(),
      sessionsIncluded: i.number().optional(),
    }),
    profiles: i.entity({
      avatarUrl: i.string().optional(),
      createdAt: i.date().optional(),
      fullName: i.string().optional(),
      isOnline: i.boolean().optional(),
      lastSeen: i.date().optional(),
      updatedAt: i.date().optional(),
      userId: i.string().unique().indexed(),
    }),
    trainingSessions: i.entity({
      adminJoined: i.boolean().optional(),
      clientEmail: i.string().optional(),
      clientFullName: i.string().optional(),
      clientJoinUrl: i.string().optional(),
      completedAt: i.date().optional(),
      createdAt: i.date().optional(),
      durationMinutes: i.number(),
      mealPlanWeeks: i.number().optional(),
      notes: i.string().optional(),
      packageId: i.string().optional(),
      pickupDeliveryInfo: i.string().optional(),
      scheduledDate: i.date().indexed(),
      sessionType: i.string().indexed(),
      status: i.string().indexed(),
      trainerId: i.string().optional(),
      updatedAt: i.date().optional(),
      userId: i.string().indexed(),
    }),
    userPackages: i.entity({
      createdAt: i.date().optional(),
      expiryDate: i.date().optional(),
      packageId: i.string().indexed(),
      packageName: i.string().optional(),
      packageType: i.string().optional(),
      purchaseDate: i.date(),
      sessionsRemaining: i.number().optional(),
      status: i.string().indexed(),
      stripeSubscriptionId: i.string().optional(),
      userId: i.string().indexed(),
      // Each client's private Google Sheet (copied from one of Cassie's two
      // master trackers). Admin-set only; not editable by the client.
      trackerUrl: i.string().optional(),
      trackerType: i.string().optional(), // 'standard' | 'competition' — informational only
      includesTraining: i.boolean().optional(), // gates the Client Training Guide
      competitionPrep: i.boolean().optional(), // gates the Competition Prep Guide
    }),
    userPreferences: i.entity({
      avatarUrl: i.string().optional(),
      createdAt: i.date().indexed(),
      dateOfBirth: i.string().optional(),
      email: i.string().indexed(),
      fitnessGoals: i.any().optional(),
      fullName: i.string(),
      lastLogin: i.date().optional(),
      location: i.string().optional(),
      notificationPreferences: i.any().optional(),
      phone: i.string().optional(),
      updatedAt: i.date().optional(),
      userId: i.string().unique().indexed(),
      workoutTime: i.string().optional(),
    }),
    checkInRequests: i.entity({
      clientEmail: i.string(),
      clientFullName: i.string(),
      createdAt: i.date().indexed(),
      message: i.string(),
      preferredResponse: i.string(),
      status: i.string().indexed(),
      topic: i.string(),
      updatedAt: i.date().optional(),
      userId: i.string().indexed(),
    }),
    clientMessages: i.entity({
      content: i.string(),
      createdAt: i.date().indexed(),
      readByClient: i.boolean().optional(),
      readByCoach: i.boolean().optional(),
      senderId: i.string().indexed(),
      senderName: i.string(),
      senderRole: i.string().indexed(),
      userId: i.string().indexed(),
    }),
    pendingEnrollments: i.entity({
      email: i.string().indexed(),
      fullName: i.string().optional(),
      packageKey: i.string(),
      packageName: i.string(),
      packageType: i.string(),
      durationDays: i.number(),
      stripeSessionId: i.string().unique().indexed(),
      stripeSubscriptionId: i.string().optional(),
      amountPaid: i.number().optional(),
      status: i.string().indexed(),
      createdAt: i.date().indexed(),
      consumedAt: i.date().optional(),
      consumedByUserId: i.string().optional(),
    }),
    weeklyCheckIns: i.entity({
      userId: i.string().indexed(),
      programType: i.string().indexed(),
      weekStartDate: i.string().indexed(),
      trainingCompleted: i.number().optional(),
      trainingGoal: i.number().optional(),
      goalStatus: i.string().optional(),
      reflection: i.string().optional(),
      measurements: i.any().optional(),
      // Legacy field: full, durable InstantDB signed URLs submitted directly
      // by the client. No longer written by new check-ins (see photoPaths
      // below) — kept only so any already-existing rows keep their shape.
      // Nothing in the app reads this field for display anymore.
      photoUrls: i.any().optional(),
      // New check-ins store $files `path` strings here instead of raw URLs.
      // The progress-photo proxy (api/progress-photo.js) resolves a path to
      // an actual file server-side, after verifying the requester owns this
      // check-in (or is admin) — the browser never receives or stores a raw
      // signed URL for these.
      photoPaths: i.any().optional(),
      coachFeedback: i.string().optional(),
      status: i.string().indexed(),
      createdAt: i.date().indexed(),
      updatedAt: i.date().optional(),
    }),
    // --- Community Center (draft, not yet deployed) ---
    // Read access and real "active client" eligibility both come from
    // userPackages.status (admin-write-only, already the live source of
    // truth for every other coaching feature) via auth.ref() in
    // instant.perms.ts — not from anything on this table. This table only
    // holds the self-service profile bits (display name, intro, avatar),
    // the one-time Community Promise acknowledgment that unlocks posting,
    // and the admin-only suspension override. A member can write their own
    // row, but the permission rules block them from ever setting `suspended`
    // on themselves (see instant.perms.ts).
    communityMembers: i.entity({
      userId: i.string().unique().indexed(),
      displayName: i.string().optional(), // real name or chosen name — member's choice, not anonymous by default
      avatarUrl: i.string().optional(),
      shortIntro: i.string().optional(),
      guidelinesAcknowledgedAt: i.date().optional(), // set once, before first post/comment
      suspended: i.boolean().optional(), // admin-only write; blocks posting/commenting/reacting, not reading
      notificationsEnabled: i.boolean().optional(), // member's own preference; absent treated as on
      createdAt: i.date().indexed(),
      updatedAt: i.date().optional(),
    }),
    // Creation goes through a trusted server endpoint, not a direct client
    // write (see instant.perms.ts) — so authorId, editWindowExpiresAt, and
    // every moderation field are stamped authoritatively server-side, never
    // trusted from the client. removedByAuthor is the member's own "delete":
    // a soft-delete flag, not a real row deletion, so comments underneath
    // can be hidden along with it while admin can still review both for
    // moderation purposes. hiddenByAdmin is the separate admin moderation
    // action for content that wasn't self-removed.
    communityPosts: i.entity({
      area: i.string().indexed(), // 'announcement' | 'conversation' | 'win'
      authorId: i.string().indexed(),
      authorDisplayName: i.string(),
      content: i.string().optional(), // optional: a post can be media-only
      mediaType: i.string().optional(), // 'photo' | 'video' | undefined
      mediaPath: i.string().optional(), // storage path under community-media/
      mediaUrl: i.string().optional(),
      videoDurationSeconds: i.number().optional(),
      commentsDisabled: i.boolean().optional(),
      hiddenByAdmin: i.boolean().optional(), // admin moderation action
      removedByAuthor: i.boolean().optional(), // the author's own "delete"; hides this post AND its comments from the community, admin can still see both
      createdAt: i.date().indexed(),
      updatedAt: i.date().optional(),
      editWindowExpiresAt: i.date().optional(), // stamped by the server at creation (now + 24h); removal (above) is never time-limited
    }),
    // An individual comment's own delete is a true row deletion (available to
    // its author any time) — only a PARENT POST's removal needs the
    // soft-delete/cascading-hide treatment above, so comments don't need
    // their own removedByAuthor flag.
    communityComments: i.entity({
      postId: i.string().indexed(),
      parentCommentId: i.string().optional().indexed(), // set for a reply to another comment
      authorId: i.string().indexed(),
      authorDisplayName: i.string(),
      content: i.string(),
      hiddenByAdmin: i.boolean().optional(),
      createdAt: i.date().indexed(),
      updatedAt: i.date().optional(),
      editWindowExpiresAt: i.date().optional(), // stamped by the server at creation (now + 24h)
    }),
    communityReactions: i.entity({
      targetType: i.string().indexed(), // 'post' | 'comment'
      targetId: i.string().indexed(),
      userId: i.string().indexed(),
      emoji: i.string(),
      createdAt: i.date().indexed(),
    }),
    communityReports: i.entity({
      targetType: i.string().indexed(), // 'post' | 'comment'
      targetId: i.string().indexed(),
      reporterId: i.string().indexed(),
      reason: i.string().optional(),
      resolved: i.boolean().optional(),
      createdAt: i.date().indexed(),
    }),
    // Cassie-mediated introductions. No contact information is ever a field
    // here — see the note in instant.perms.ts. status moves
    // pending -> accepted/declined (by the recipient, privately) and then
    // Cassie closes it out by hand once she's made (or declined to make)
    // the actual introduction outside the app.
    //
    // pendingPairKey is the real, concurrency-safe duplicate-request guard:
    // a unique index InstantDB enforces at the database level, not just an
    // app-level "check, then insert" (which has a race condition under
    // simultaneous requests — two requests can both pass the check before
    // either has written). The server endpoint sets it to
    // "{requesterId}:{recipientId}" only while status is 'pending', and
    // clears it (sets to null/absent) the moment the request is accepted,
    // declined, or closed, freeing that pair up for a future request.
    communityIntroductionRequests: i.entity({
      requesterId: i.string().indexed(),
      recipientId: i.string().indexed(),
      note: i.string().optional(), // optional message from requester, e.g. why they'd like to connect
      status: i.string().indexed(), // 'pending' | 'accepted' | 'declined' | 'closed'
      pendingPairKey: i.string().optional().unique().indexed(),
      createdAt: i.date().indexed(),
      respondedAt: i.date().optional(),
      closedAt: i.date().optional(), // set by admin once handled
      // Admin-only (set by admin directly; the recipient's own update is
      // already restricted to status/respondedAt in instant.perms.ts, so
      // these two fields are unreachable by anyone but admin by construction,
      // not by a separate rule). Drives the request-endpoint's cooldown
      // logic, not a permission rule — see the note in instant.perms.ts.
      closedReason: i.string().optional(), // e.g. 'handled' | 'inappropriate'
      adminAllowedAgainAt: i.date().optional(), // explicit override lifting an indefinite block, from this moment forward
    }),
  },
  links: {
    $streams$files: {
      forward: {
        on: "$streams",
        has: "many",
        label: "$files",
      },
      reverse: {
        on: "$files",
        has: "one",
        label: "$stream",
        onDelete: "cascade",
      },
    },
    $usersLinkedPrimaryUser: {
      forward: {
        on: "$users",
        has: "one",
        label: "linkedPrimaryUser",
        onDelete: "cascade",
      },
      reverse: {
        on: "$users",
        has: "many",
        label: "linkedGuestUsers",
      },
    },
    chatMessagesUser: {
      forward: {
        on: "chatMessages",
        has: "one",
        label: "user",
      },
      reverse: {
        on: "$users",
        has: "many",
        label: "chatMessages",
      },
    },
    clientMessagesUser: {
      forward: {
        on: "clientMessages",
        has: "one",
        label: "user",
      },
      reverse: {
        on: "$users",
        has: "many",
        label: "clientMessages",
      },
    },
    clientRecommendationsUser: {
      forward: {
        on: "clientRecommendations",
        has: "one",
        label: "user",
        onDelete: "cascade",
      },
      reverse: {
        on: "$users",
        has: "one",
        label: "recommendation",
      },
    },
    profilesUser: {
      forward: {
        on: "profiles",
        has: "one",
        label: "user",
        onDelete: "cascade",
      },
      reverse: {
        on: "$users",
        has: "one",
        label: "profile",
      },
    },
    trainingSessionsUser: {
      forward: {
        on: "trainingSessions",
        has: "one",
        label: "user",
      },
      reverse: {
        on: "$users",
        has: "many",
        label: "sessions",
      },
    },
    userPackagesPackage: {
      forward: {
        on: "userPackages",
        has: "one",
        label: "package",
      },
      reverse: {
        on: "packages",
        has: "many",
        label: "userPackages",
      },
    },
    userPackagesUser: {
      forward: {
        on: "userPackages",
        has: "one",
        label: "user",
      },
      reverse: {
        on: "$users",
        has: "many",
        label: "packages",
      },
    },
    userPreferencesUser: {
      forward: {
        on: "userPreferences",
        has: "one",
        label: "user",
        onDelete: "cascade",
      },
      reverse: {
        on: "$users",
        has: "one",
        label: "preferences",
      },
    },
    weeklyCheckInsUser: {
      forward: {
        on: "weeklyCheckIns",
        has: "one",
        label: "user",
      },
      reverse: {
        on: "$users",
        has: "many",
        label: "weeklyCheckIns",
      },
    },
    communityMembersUser: {
      forward: {
        on: "communityMembers",
        has: "one",
        label: "user",
      },
      reverse: {
        on: "$users",
        has: "one",
        label: "communityMember",
      },
    },
    communityPostsAuthor: {
      forward: {
        on: "communityPosts",
        has: "one",
        label: "author",
      },
      reverse: {
        on: "$users",
        has: "many",
        label: "communityPosts",
      },
    },
    // communityComments -> communityPosts is a real schema link (not just a
    // plain postId field) specifically so the comment's view permission can
    // do data.ref('post.removedByAuthor') / data.ref('post.hiddenByAdmin') —
    // a comment on a removed or hidden post disappears along with it for
    // everyone but admin. See instant.perms.ts. data.ref() on a has-one link
    // from a non-auth entity is documented but unverified against this app's
    // data — part of the sandbox test plan.
    communityCommentsPost: {
      forward: {
        on: "communityComments",
        has: "one",
        label: "post",
      },
      reverse: {
        on: "communityPosts",
        has: "many",
        label: "comments",
      },
    },
    // No schema-level link for communityReports/communityReactions/
    // communityIntroductionRequests targets — they reference each other by
    // plain id fields (targetId, requesterId, recipientId) checked directly
    // in permission rules, the same pattern already used elsewhere (e.g.
    // clientMessages.userId). A report's target can be either a post or a
    // comment, so a single typed link wouldn't fit both anyway.
  },
  rooms: {},
});

// This helps TypeScript display nicer intellisense
type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type { AppSchema };
export default schema;
